import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { DEFAULT_UNITS_PREFERENCE, type Exercise } from '@heraion/shared';

import { IconButton } from '../primitives/IconButton';
import type { WeightUnit } from './state/loggerDraft';
import { useTheme } from './theme';
import { formatUnitValue, weightLocale } from './plateCalcMath';
import { unitWord } from '../weightUnitLabels';
import { makeStyles } from './plateCalcModalStyles';
import { PlateCalcCalculatorSection } from './PlateCalcCalculatorSection';
import { PlateCalcBarbellBody } from './PlateCalcBarbellBody';
import { DumbbellStepSection } from './DumbbellStepSection';
import { usePlateCalcModalState } from './usePlateCalcModalState';

// Plate-loading helper. Mounted from the per-entry "Plates" button.
// Presentation around `computePlates` from packages/shared — the user types a
// target (pre-seeded with the most recent set's weight from the row that
// opened the modal), picks how the bar is loaded, picks the bar, and edits the
// per-size pair count of the home plate set. All three prefs persist so the
// modal remembers them across sessions and across workouts.
//
// ⭐ LG-37b fetta E1 — the modal speaks the ROW's unit (Antenore, inbox
// 66c51036: «ho provato a switchare a libre nel WO in corso, aperto il
// configuratore dei pesi ed è tutto in Kg»). Every number on this screen is in
// `unit`; canonical kilos exist at two borders only — `initialKg` in, `onApply`
// out — because that is what the draft persists.
//
// LG-37b fetta A — the LOADING SHAPE became a control (Antenore 10/09).
// It used to be derived and unchangeable: `exercise?.barLoading ===
// 'single-side'`. That read a catalog field which, until the 10/09 catalog
// round, was valued ZERO times on 405 rows — so the whole single-side mode was
// finished, translated, and reachable by nobody, and a landmine press told the
// athlete to load "per side" on a bar loaded at one end. The catalog now carries
// the field on 13 rows, but a catalog that is one row for everyone can never
// know YOUR gym: the athlete has to be able to say it.
//
// ⇒ Precedence: the athlete's stored choice for THIS exercise wins, else the
// catalog seed, else pairs. Stored per slug on the device — no write ever
// reaches the production catalog, which is what keeps this slice reversible.
//
// LG-37b fetta B — the modal became BIDIRECTIONAL (Antenore 10/09).
// There is one working value and, now, two ways to change it: type it, or
// compose it by tapping plates. Composing is the direction that was missing —
// dischi → totale — and it is the one you want with the bar already loaded.
//
// ⚠️ Composing needs its own state, and the reason is worth writing down: the
// greedy is free to re-express any total the way IT prefers. Tap 15 on a bar
// that reads 60 and the total is right (90) while the greedy's answer for 90
// is 25 + 10 — the 15 you just tapped is nowhere. So while a composition is
// live it IS the summary (`compositionToCalculation`), and typing in the field
// drops it back to the greedy. One value, two directions, never both at once.
//
// ⚠️ The shape is two-valued here, not the three of the design. The third —
// belt/assist — is the bodyweight ± this modal already has, and bodyweight rows
// CANNOT reach this modal today: FocusPanelInputs.tsx:419 withholds
// `onOpenPlates` from them, and un-withholding it is fetta D. Shipping a third
// chip now would build one more control nobody can reach, which is the exact
// defect this item exists to fix. It lands with D.

type Props = {
  visible: boolean;
  /** The value the modal opens on, in **canonical kilos** — it comes from the
   *  draft, which persists kg whatever the row displays. Converted to `unit`
   *  on the way in; nothing downstream of the seed is kilos. */
  initialKg: number;
  /** The unit of the SET the modal was opened from.
   *
   *  ⛔ Not `profile.unitsPreference`: `toggle_unit` is a per-row action
   *  (loggerActions.ts:228), so reading the profile would leave this modal deaf
   *  to the exact gesture that opened inbox 66c51036 — and a modal showing kg
   *  while Apply writes lb is LG-81 again.
   *
   *  Falls back to DEFAULT_UNITS_PREFERENCE: the visual bench and the tests mount this modal
   *  on their own, without a row to ask. */
  unit?: WeightUnit;
  /** Exercise the modal was opened from — feeds the bar default
   *  (`Exercise.defaultBarKg`), the loading-shape seed (`Exercise.barLoading`)
   *  and the slug the athlete's shape choice is remembered under. Optional so
   *  the modal still works in flows that don't have a catalog row to hand. */
  exercise?: Exercise | null;
  /** Bodyweight mode (2026-05-23 P.M.). Shows a sign toggle (Add / Assist)
   *  so the row's EXTRA value can be loaded as positive (weighted pull-up
   *  belt) or negative (band-assisted). When false, the modal works as
   *  before — pure plate calculator for barbell loading. */
  isBodyweight?: boolean;
  /** Pre-seeded sign for bodyweight mode — mirrors set.extraNegative so
   *  reopening the modal on an already-loaded set restores the previous
   *  choice. Ignored when isBodyweight=false. */
  initialNegative?: boolean;
  onClose: () => void;
  /** When provided, the modal renders an Apply CTA that writes the
   *  current target back to the caller. Signed: positive for add /
   *  weighted, negative for assist (only relevant in bodyweight mode).
   *  In weighted mode the value is always positive. */
  onApply?: (signedKg: number) => void;
};

export function PlateCalcModal({
  visible,
  initialKg,
  unit: seedUnit = DEFAULT_UNITS_PREFERENCE,
  exercise = null,
  isBodyweight = false,
  initialNegative = false,
  onClose,
  onApply,
}: Props) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  // What the calculator's «Copia nel peso» writes into the field — the same
  // decimal mark the athlete's keyboard would have typed.
  const locale = weightLocale(i18n);
  const {
    target,
    targetKg,
    bar,
    barOptions,
    inventory,
    loadShape,
    negative,
    setNegative,
    mode,
    calc,
    onSelectBar,
    onSelectShape,
    onEditTarget,
    setPairs,
    onComposeDelta,
    openKey,
    // ⭐ LG-93 — l'unità che il modal MOSTRA arriva dal hook, non dalla prop:
    // la prop è solo il seme del set, e ruotarla è una scelta di vista.
    unit,
    cycleUnit,
    snapToLoadable,
    calculator,
    isHandheldLift,
  } = usePlateCalcModalState({
    visible,
    initialKg,
    unit: seedUnit,
    exercise,
    isBodyweight,
    initialNegative,
  });
  // ⛔ The calculator section below must keep being MOUNTED and UNMOUNTED by
  // `calculator.expanded`, not hidden. Its seed is a lazy `useState`
  // initialiser, so it only re-reads the current weight because the component
  // is built again each time it opens. Keeping it mounted (for a slide-in,
  // say) would freeze the seed on whatever the field said the first time, and
  // nothing would say so. The disclosure lives in the state hook (it folds on
  // open there, LG-87).

  const styles = makeStyles(theme);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <View style={styles.header}>
          <Pressable accessibilityRole="button" onPress={onClose}>
            <Text style={styles.cancel}>{t('mobile.plates.close')}</Text>
          </Pressable>
          <Text style={styles.title}>
            {isHandheldLift
              ? t('mobile.plates.titleDumbbell')
              : t('mobile.plates.title')}
          </Text>
          {onApply ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('mobile.plates.apply', {
                defaultValue: 'Apply',
              })}
              onPress={() => {
                if (!Number.isFinite(targetKg) || targetKg <= 0) {
                  onClose();
                  return;
                }
                const signed =
                  isBodyweight && negative ? -targetKg : targetKg;
                onApply(signed);
              }}
            >
              <Text style={styles.apply}>
                {t('mobile.plates.apply', { defaultValue: 'Apply' })}
              </Text>
            </Pressable>
          ) : (
            <View style={styles.headerSpacer} />
          )}
        </View>
        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ScrollView
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.inputRow}>
              <Text style={styles.inputLabel}>
                {t('mobile.plates.targetLabel')}
              </Text>
              <TextInput
                style={styles.input}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder="—"
                placeholderTextColor={theme.palette.ink3}
                value={target}
                onChangeText={onEditTarget}
                autoFocus
              />
              {/* ⭐ LG-93 — l'unità non è più una SCRITTA, è il controllo che
                  la cambia. Sua parola dell'11/09: «il kg non è più una label
                  ma diventa uno switcher, ogni tap cambia unità a rotazione».
                  ⛔ Ruota su `UNIT_CYCLE`, non fa toggle fra due: con due unità
                  si comporta identico, con tre continua a funzionare.
                  ⭐ `IconButton` con `label` e non un Pressable a mano: il box
                  da 36, l'hitSlop e gli stati premuti sono già suoi — ed è lo
                  stesso primitivo del bottone calcolatrice qui accanto.
                  ⚠️ `unitWord` resta l'unica casa di «come questa app scrive kg
                  e lb»: un gate di drift non vede una chiave dinamica. */}
              <IconButton
                theme={theme}
                label={unitWord(t, unit)}
                onPress={cycleUnit}
                // ⚠️ Solo verticale. Gli IconButton hanno 8pt di skirt per
                // lato e il gap della riga è 8: le zone dei vicini si
                // TOCCHEREBBERO, e qui i tre bottoni fanno cose diverse —
                // convertire, arrotondare, aprire. Un tap vicino alla cucitura
                // prenderebbe quello sbagliato in silenzio, e il numero
                // cambierebbe lo stesso
                // [[feedback_hitslop_between_opposite_controls_is_a_trap]].
                hitSlop={{ top: 8, bottom: 8 }}
                accessibilityLabel={t('mobile.logger.unitA11y', {
                  unit: unitWord(t, unit),
                })}
              />
              {/* ⭐ LG-93 — «≈»: porta il numero al carico più vicino che si
                  può davvero mettere sul ferro, con il passo che esce
                  dall'inventario dell'atleta e per FORMA di carico.
                  ⛔ Sta QUI e non dentro la calcolatrice, ed è la correzione
                  di un fraintendimento mio: là il numero onesto è quello vero,
                  e l'atleta che compone i dischi vuole vedere quanto ha messo,
                  non quanto avrebbe potuto mettere. */}
              <IconButton
                theme={theme}
                name="equal-approximately"
                onPress={snapToLoadable}
                // ⚠️ Solo verticale. Gli IconButton hanno 8pt di skirt per
                // lato e il gap della riga è 8: le zone dei vicini si
                // TOCCHEREBBERO, e qui i tre bottoni fanno cose diverse —
                // convertire, arrotondare, aprire. Un tap vicino alla cucitura
                // prenderebbe quello sbagliato in silenzio, e il numero
                // cambierebbe lo stesso
                // [[feedback_hitslop_between_opposite_controls_is_a_trap]].
                hitSlop={{ top: 8, bottom: 8 }}
                accessibilityLabel={t('mobile.plates.roundToLoadable', {
                  defaultValue: 'Round to a loadable weight',
                })}
              />

              {/* ⭐ Beside the weight, not at the foot of the screen — his
                  correction of 10/09: «un'icona calcolatrice ben visibile di
                  fianco al peso come bottone invece di mettere il link in
                  fondo». An affordance at the bottom of a long scroller is
                  one nobody finds, and this is the only thing in the modal
                  that has nothing to do with the plate solver. */}
              <IconButton
                theme={theme}
                name="calculator"
                onPress={calculator.toggle}
                active={calculator.expanded}
                expanded={calculator.expanded}
                // ⚠️ Solo verticale. Gli IconButton hanno 8pt di skirt per
                // lato e il gap della riga è 8: le zone dei vicini si
                // TOCCHEREBBERO, e qui i tre bottoni fanno cose diverse —
                // convertire, arrotondare, aprire. Un tap vicino alla cucitura
                // prenderebbe quello sbagliato in silenzio, e il numero
                // cambierebbe lo stesso
                // [[feedback_hitslop_between_opposite_controls_is_a_trap]].
                hitSlop={{ top: 8, bottom: 8 }}
                accessibilityLabel={t('mobile.plates.calcOpen')}
              />
            </View>

            {calculator.expanded ? (
              <PlateCalcCalculatorSection
                // ⭐ Rinasce a ogni apertura del modal: resta aperta se l'hai
                // lasciata aperta, ma riparte dal peso di QUESTA riga.
                // ⛔ Il prefisso non è decorazione: i due figli che portano
                // `openKey` sono FRATELLI nello stesso array di reconciliation,
                // e una chiave condivisa fa gridare React in console («two
                // children with the same key»). Oggi è innocuo solo perché i
                // tipi differiscono; smette di esserlo il giorno in cui uno dei
                // due diventa condizionale. Preso dalla REVIEW, dentro il mio
                // stesso test.
                key={`calc-${openKey}`}
                theme={theme}
                styles={styles}
                // ⭐ Opens ON the weight that is already there (Antenore,
                // 11/09: «a rigor di logica»). Not `initialKg`, which is the
                // value the modal OPENED with: by now the athlete may have
                // typed or composed, and the calculator has to continue from
                // what the field says now. The section unmounts when it
                // collapses, so this re-seeds on every open.
                // ⚠️ Canonical kilos, and deliberately so even though the field
                // no longer is: `CalcState.placedKg` is the value that rides
                // along a kg⇄lb swap so swapping back lands where it started
                // (`LG-85`), and that value has to be unit-free. The section
                // converts it to `workingUnit` for what it DISPLAYS.
                seedKg={targetKg}
                workingUnit={unit}
                onClose={calculator.toggle}
                // Copying is the athlete typing the number by another hand, so
                // it goes through the same door: it drops any live composition
                // and hands the value back to the greedy. ⭐ It arrives already
                // in the row's unit — no reconversion here, which is exactly
                // what stopped being true in fetta E1.
                onCopy={(value) => {
                  onEditTarget(formatUnitValue(value, unit, locale));
                  calculator.toggle();
                }}
              />
            ) : null}

            {isHandheldLift ? (
              <DumbbellStepSection theme={theme} styles={styles} unit={unit} />
            ) : (
              <PlateCalcBarbellBody
                theme={theme}
                styles={styles}
                isBodyweight={isBodyweight}
                negative={negative}
                setNegative={setNegative}
                loadShape={loadShape}
                onSelectShape={onSelectShape}
                inventory={inventory}
                calc={calc}
                mode={mode}
                unit={unit}
                bar={bar}
                barOptions={barOptions}
                onSelectBar={onSelectBar}
                onComposeDelta={onComposeDelta}
                setPairs={setPairs}
                openKey={openKey}
              />
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
}
